import { useEffect } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, FlaskConical, ArrowRight, Clock, FileText } from "lucide-react";
import { PAPERS, papersByTrack, TRACK_LABELS } from "@/data/research";
import { useSeo } from "@/lib/seo";

const TRACKS = ["methodology", "patterns", "tech", "studies"];

export default function QuantLab() {
  useSeo({
    title: "TradrQuant Quant Lab — Crypto Trading Research & Methodology",
    description:
      "Original quant research and methodology whitepapers behind the TradrQuant engine: the dual-gate confluence model, Quant Force adaptive baseline, non-repainting architecture, expectancy, multi-timeframe confluence and market-regime studies.",
    path: "/research",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="quant-lab" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link
            to="/"
            data-testid="lab-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300"
          >
            <ChevronLeft size={14} /> cd ~/terminal
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 md:pt-16">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">
          <FlaskConical size={14} /> // tradrquant quant lab
        </p>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
          The research behind <span className="text-[#7D8390]">the engine.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm text-white/50 leading-relaxed">
          Original methodology and engineering whitepapers on how TradrQuant qualifies setups — the
          dual-gate confluence model, the Quant Force adaptive baseline, non-repainting architecture
          and market-regime studies. {PAPERS.length} papers. Figures are illustrative; not financial advice.
        </p>
      </div>

      <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-12 md:py-16 space-y-14">
        {TRACKS.map((track, ti) => (
          <section key={track} data-testid={`lab-track-${track}`}>
            <div className="flex items-center gap-3 border-b border-white/10 pb-3">
              <span className="font-mono text-[11px] tabular-nums text-white/25">0{ti + 1}</span>
              <h2 className="font-display font-semibold tracking-[-0.03em] text-lg sm:text-xl">{TRACK_LABELS[track]}</h2>
            </div>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {papersByTrack(track).map((p, i) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Link
                    to={`/research/${p.slug}`}
                    data-testid={`lab-paper-${p.slug}`}
                    className="group flex h-full flex-col border border-white/10 bg-[#13151A] p-6 hover:border-[#BACD1C]/40 transition-colors duration-300"
                  >
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390]">
                      <FileText size={12} /> Whitepaper
                    </div>
                    <h3 className="mt-3 font-display font-semibold tracking-[-0.03em] text-lg leading-snug group-hover:text-[#BACD1C] transition-colors">{p.title}</h3>
                    <p className="mt-2.5 flex-1 font-mono text-[12px] text-white/45 leading-relaxed">{p.subtitle}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30"><Clock size={11} /> {p.readMins} min read</span>
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7D8390] group-hover:text-[#BACD1C]">Read <ArrowRight size={11} /></span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        ))}

        <p className="font-mono text-[11px] text-white/25">
          Educational & methodology content only — not financial advice. Figures are conceptual and illustrative;
          they do not represent verified trading results.
        </p>
      </div>
      <Footer />
    </div>
  );
}
